import React, { useState, useContext, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../../../axiosInstance';
import logoSilver from '../Assets/logo_silver.png';
import { UserContext } from '../../../UserContext';
import { AtomSpinner } from 'react-epic-spinners';
import AlertBanner from '../Banner/AlertBanner';
import './AccountType.css';
import { 
  FaBookOpen, 
  FaGraduationCap, 
  FaMagic,
  FaRocket,
  FaCrown,
  FaInfinity,
  FaShieldAlt,
  FaCheckDouble,
  
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

const AccountType = () => {
  const navigate = useNavigate();
  const { username, email, setIsStudent } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef(null);
  const cardsRef = useRef([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [fromLoginSignup, setFromLoginSignup] = useState(false);
  const [mode, setMode] = useState("creative");

  const toggleMode = () => {
    setMode(prev =>
      prev === "creative" ? "professional" : "creative"
    );
  };
  useEffect(() => {
    const savedMode = localStorage.getItem("accountPageMode");
    if (savedMode) {
      setMode(savedMode);
    }
  }, []);

  /* Save mode when it changes */
  useEffect(() => {
    localStorage.setItem("accountPageMode", mode);
  }, [mode]);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };

    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = (scrollTop / scrollHeight) * 100;
        setScrollProgress(progress);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleStudentTypeClick = async () => {
    if (loading) return;

    // --- Clear all storage to prevent any leftover state ---
    sessionStorage.clear();
    localStorage.clear();
    setIsStudent(null);

    // --- Set the new student status ---
    setSelectedType('student');
    setLoading(true);

    const newIsStudent = true;
    setIsStudent(newIsStudent);
    sessionStorage.setItem('isStudent', String(newIsStudent));

    // Cinematic delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Navigate to unrestricted SAT hook
    navigate('/sat-hook');
  };



  const handleProfessionalTypeClick = async () => {
    if (loading) return;
    setSelectedType('professional');
    setActiveVideo('professional');
    setLoading(true);

    const newIsStudent = false;
    setIsStudent(newIsStudent);
    sessionStorage.setItem('isStudent', String(newIsStudent));

    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      if (fromLoginSignup && username && email) {
        const values = {
          username,
          email,
          role: 'user'
        };
        const res = await axiosInstance.post(`${API_URL}/users/signup`, values);
        if (res.data.ok) {
          navigate('/price-calculator');
        } else {
          alert(res.data.message);
        }
      } else {
        navigate('/hookb');
      }
    } catch (err) {
      console.error('Error signing up:', err);
      setShowAlert(true);
    } finally {
      setLoading(false);
      setSelectedType(null);
      setActiveVideo(null);
    }
  };

  const handleCreativeTypeClick = async (fromRoot = false) => {
    if (loading) return;

    setSelectedType('creative');
    setActiveVideo('creative');
    setLoading(true);

    const newIsStudent = false;
    setIsStudent(newIsStudent);
    sessionStorage.setItem('isStudent', String(newIsStudent));
    sessionStorage.setItem('accountType', 'creative');

    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      const storedEmail = localStorage.getItem('email');
      const isGuest = !storedEmail; // only true if no email stored

      if (fromLoginSignup && username && email) {
        // logged-in user, not a guest
        const values = {
          username,
          email,
          role: 'creative',
          isGuest: false,  // explicitly false
          fromRoot,
        };
        const res = await axiosInstance.post(`${API_URL}/users/signup`, values);
        if (res.data.ok) {
          navigate('/creator-dashboard', { state: { isGuest: false, fromRoot } });
        }
      } else {
        // guest or preview user
        navigate('/price-predictor', { state: { isGuest, fromRoot } });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setSelectedType(null);
      setActiveVideo(null);
    }
  };

  const isPrerendering = typeof window === 'undefined' || window.__PRERENDERING;
  if (typeof window === 'undefined' || isPrerendering) {
    return isPrerendering ? <div id="prerender-placeholder"></div> : null;
  }

  const paths = [
    {
      id: 'professional',
      title: 'PROFESSIONALS',
      color: '#FA60A8',
      gradient: 'linear-gradient(135deg, #FA60A8 0%, #FCCF31 100%)',
      stats: [
        { icon: <FaGraduationCap />, title: 'PROFESSIONALS', value: '1,480+', label: 'Lives Transformed' }
      ],
      features: [
        '1.5K+ Global Reach',
        'Career Optimization',
        'Dispute Resolution',
        'Document Preparation',
        '24/7 Online Support',
        'Borderless Network'
      ],
      videoThumb: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      testimonial: {
        quote: 'OWCP won on appeal. Persistence pays off.',
        author: 'United States, Federal Worker'
      }
    },
    {
      id: 'creative',
      title: 'CREATIVES',
      color: ' #895cf1',
      gradient: 'linear-gradient(135deg, #00DBDE 0%, #FC00FF 100%)',
      stats: [
        { icon: <FaMagic />, title: 'CREATIVES', value: '50k+', label: 'Assets Created' }
      ],
      features: [
        '85M+ Online Community',
        'Collaborative Creator Dashboard',
        'Experimental Lab',
        'Searchable Library',
        'Hyper-Realistic 3D Content',
        'Smart Contracts'
      ],
      videoThumb: '',
      testimonial: {
        quote: 'My digital art generates passive income now.',
        author: 'United States, Digital Artist'
      }
    },
    {
      id: 'student',
      title: 'STUDENTS',
      color: '#FA60A8',
      gradient: 'linear-gradient(135deg, #4158D0 0%, #C850C0 46%, #FFCC70 100%)',
      stats: [
        { icon: <FaBookOpen />, title: 'STUDENTS', value: '70+', label: 'Point Score Increase' }
      ],
      features: [
        '1K+ Active Users',
        'Adaptive Learning Algorithm',
        '60-80 Point Score Increase',
        'Official SAT Practice Tests',
        'Certified Digital Transcripts',
        'College Application Assistance'
      ],
      videoThumb: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      testimonial: {
        quote: 'Increased my score by 120 points in 90 days.',
        author: "Honduras, Student"
      }
    }
  ];
  

  return (
    <div className={`account-type-page ${mode}-mode`} ref={containerRef}>
      <Helmet>
        <title>Account Type | Contranorm Media & Production</title>
        <meta
          name="description"
          content="Join the elite. Select your path to unlimited potential. Student, Professional, or Creative — your future starts now."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://contranormdigital.com/account-type" />
      </Helmet>

      {/* IMMERSIVE BACKGROUND */}
      <div className="cosmic-bg">
        <div className="stars" />
        <div className="nebula" />
        <div className="particles">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                width: `${Math.random() * 3}px`,
                height: `${Math.random() * 3}px`,
              }}
            />
          ))}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="destiny-container">
        {/* HERO */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="pre-title">
              <hr className="section-divider-animate" role="separator" aria-hidden="true" />
              <a href="#">
                <img src={logoSilver} alt="Logo Silver" className="cmp-logo" />
              </a>
             
              <hr className="section-divider-animate" role="separator" aria-hidden="true" />
             
            </div>
             

            <h1 className="main-title">
              <span className="title-line">THE SOLUTION</span>
              <span className="title-line gradient-text"></span>
            </h1>

            <p className="hero-description">
              This changes everything. Three paths. Unlimited potential. Your next move matters more than any past misstep. If you’re serious about results, discover what actually works.
            </p>

            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-value">87%</span>
                <span className="stat-label">Success Rate</span>
              </div>
              <div className="hero-stat">
                <span className="stat-value">5K+</span>
                <span className="stat-label">Ready-to-Use Resources</span>
              </div>
              <div className="hero-stat">
                <span className="stat-value">320+ </span>
                <span className="stat-label">Hours Saved</span>
              </div>
            </div>
          </div>
        </section>

        {/* PATHS */}
        <section className="paths-grid">
          {paths.map((path, index) => (
            <article
              key={path.id}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`path-card ${
                selectedType === path.id ? 'selected' : ''
              } ${activeVideo === path.id ? 'video-active' : ''}`}
              style={{
                '--mouse-x': `${mousePosition.x}px`,
                '--mouse-y': `${mousePosition.y}px`,
                '--card-color': path.color,
                '--card-gradient': path.gradient,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (path.id === 'student') handleStudentTypeClick();
                else if (path.id === 'professional') handleProfessionalTypeClick();
                else if (path.id === 'creative') handleCreativeTypeClick();
              }}
            >
              <div className="card-glow" />
                <span className='path-sliver'></span> 
              <div className="card-content">
                
                {path.stats.map((stat, i) => (
                  <div className="stat-item" key={i}>
                    <span className="stat-icon">{stat.icon}</span>
                    <span className="stat-value">{stat.title}</span>
                    
                  </div>
                ))}
              

                <ul className="featured-list">
                  {path.features.map((feature, i) => (
                    <li key={i}>
                      <FaCheckDouble className="featured-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="testimonial">
                  <p className="quote">"{path.testimonial.quote}"</p>
                  <p className="author">— {path.testimonial.author}</p>
                </div>
              </div>
            </article>
          ))}
        </section>
        
        {/* TRUST */}
        <section className="bottom-cta">
          <div className="trust-indicators">
            <div className="trust-item">
              <FaShieldAlt />
              <span>Professional Advocacy</span>
            </div>
            <div className="trust-item">
              <FaRocket />
              <span>Immediate Results</span>
            </div>
            <div className="trust-item">
              <FaCrown />
              <span>Premium Support 24/7</span>
            </div>
          </div>

          <p className="change-mind">
            <FaInfinity className="infinity-icon" />
            Login & Upgrade at Anytime for Full Access.
          </p>
        </section>
        
        
      </main>
      <div className="paypal-wrapper">
        
        <img 
          src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_200x51.png" 
          alt="Powered by PayPal" 
          className="paypal-logo"
        />
        
      </div>
      <div className="social-links">
        <a href="#" aria-label="Twitter">
          <i className="fab fa-twitter"></i>
        </a>
        <a href="#" aria-label="Discord">
          <i className="fab fa-discord"></i>
        </a>
        <a href="#" aria-label="Instagram">
          <i className="fab fa-instagram"></i>
        </a>
        <a href="#" aria-label="Patreon">
          <i className="fab fa-patreon"></i>
        </a>
        <a href="#"><i><img 
          src="https://altcoinsbox.com/wp-content/uploads/2023/03/metamask-grayscale-logo-300x300.webp" 
          alt="Metamask Wallet" 
          className="metamask-logo"
        /></i></a>
      </div>
      
      <div className="site-footer">
                
        <p>
          Contranorm Media & Production &trade; <br />
          <a href="https://compliance.contranormdigital.com/privacy-policy/index.html" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>{" "} | {" "}
          <a href="https://compliance.contranormdigital.com/terms-of-service/index.html" target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>{" "} | {" "}
          <a href="https://compliance.contranormdigital.com/contact-us/index.html" target="_blank" rel="noopener noreferrer">
            Contact Us
          </a>{" "} | {" "}
          <Link to="/login-signup">Login</Link>
        </p>
        <div className="footer-strip">
          <div className="copyright">All Rights Reserved.  {new Date().getFullYear()}</div>
        </div>  
      </div>
      <div className="mode-toggle">
        <button onClick={toggleMode}>
          {mode === "creative" ? "Professional Mode" : "Creative Mode"}
        </button>
      </div>
    
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <AtomSpinner color="#FA60A8" size={60} />
            <h3 className="loading-title">
              {selectedType === 'student' && 'INITIALIZING SAT PROTOCOL'}
              {selectedType === 'professional' && 'ACTIVATING EXECUTIVE SUITE'}
              {selectedType === 'creative' && 'ACTUALIZING UPDATES'}
            </h3>
            <p className="loading-subtitle">
              Preparing your personalized dashboard...
            </p>
          </div>
        </div>
      )}

      {/* ALERT */}
      <AlertBanner
        showAlert={showAlert}
        showSaveButton={false}
        handleCancel={() => setShowAlert(false)}
      />
      
      
    </div>
  );
  
};

export default AccountType;